import i18n from '@/plugins/vue-i18n';
import menuLevel from '@enums/reporting-data-level';
import to from 'await-to-js';
import axios from 'axios';
import { each, flatten, get, keyBy, map, omit, partition, reduce, size, sortBy } from 'lodash';
import { defineStore } from 'pinia';
import compoundExtractFilters from '@enums/compound-extract-filters';
import { useCompoundExtract } from '@/composables/use-compound-extract';
// TODO: aov3-8283 fix circular dependency issue
// eslint-disable-next-line import/no-cycle
import { useWorkpackageScenarioSelectStore } from './workpackage-scenario-select';

export const useCompoundExtractStore = defineStore('compoundExtract', {
  state: () => ({
    canvases: [],
    storeClasses: [],
    clusters: [],
    checkpoints: [],
    extractSeries: [],
    extractSettings: [],
    extracts: [],
    previousExtracts: [],
    selectedRows: [],
    selectedExtract: null,
    selectedStep: null,
    selectedExtractFilter: compoundExtractFilters.all,
    loading: false,
    isProcessing: false,
  }),
  getters: {
    getSelectedExtractStatus() {
      return get(this.selectedExtract, 'series.status', {});
    },

    extractSeriesIdBySettingId() {
      return reduce(
        this.extractSeries,
        (acc, series) => {
          each(series.extractSettingsIds, id => {
            acc[id] = series._id;
          });
          return acc;
        },
        {}
      );
    },

    extractSeriesById() {
      return keyBy(this.extractSeries, '_id');
    },

    getExtractById() {
      return id => this.extracts.find(({ _id }) => _id === id);
    },

    storeClassesById() {
      return keyBy(flatten(map(this.storeClasses, 'storeClasses')), '_id');
    },

    clustersById() {
      return keyBy(flatten(map(this.clusters, 'clusters')), 'clusterId');
    },

    getCanvasName() {
      return entry => {
        if (size(entry.description)) return entry.description;

        // Handle when the canvas storeclass was deleted but canvas is still available, this safety check on the 'name' should be deleted once storeclass uses dependency tree on AOV3-1191
        const storeClassName = get(
          this.storeClassesById[entry.storeClassId],
          'name',
          i18n.t('general.notAvailable')
        );

        const clusterName =
          entry.clusterId === null
            ? i18n.t('reportingPage.unclustered')
            : this.clustersById[entry.clusterId].clusterName;
        return `${storeClassName} - ${clusterName}`;
      };
    },

    getFinalCanvases(state) {
      return state.canvases.filter(canvas => canvas.isFinalCheckpoint);
    },

    getFinalCheckpoints(state) {
      return state.checkpoints.filter(checkpoint => checkpoint.isFinalCheckpoint);
    },
  },
  actions: {
    async generateExtract(extractParams) {
      const [err, response] = await to(axios.post('/api/compound-extract/', extractParams));
      if (err) throw new Error(err.message);

      this.extractSeries.push(response.data);
      this.selectedExtract = response.data;

      return response.data;
    },

    async updateExtract(extractData) {
      // TODO: ensure that extractData.settings are properly passed (sometimes they are not which is causing errors)
      let seriesId = null;
      // state.selectedExtract is expected to be set in all places apart of extract update on extract main page
      // when sending file to the ftp, in this case seriesId is passed in payload.
      if (this.selectedExtract) {
        extractData.settings._id = this.selectedExtract.settings._id;
        seriesId = this.selectedExtract.series._id;
      } else {
        seriesId = extractData.settings.series._id;
      }
      const [err] = await to(axios.patch(`/api/compound-extract/${seriesId}`, { extractData }));
      if (err) throw new Error(err.message);
      // TODO: move snackbar
      // dispatch('snackbar/showSuccess', i18n.t('actions.saveSuccess'), { root: true });
      if (this.selectedExtract) this.fetchExtractSeriesById(seriesId);
      return true;
    },

    // Gets extract series configuration
    async fetchExtractSeries({ extractTypes } = {}) {
      const [err, response] = await to(
        axios.get('/api/compound-extract/', { params: { extractTypes } })
      );
      if (err) throw new Error(err.message);

      this.extractSeries = response.data;
    },

    async fetchExtractSeriesById(extractSeriesId) {
      const [err, response] = await to(axios.get(`/api/compound-extract/${extractSeriesId}`));
      if (err) throw new Error(err.message);
      this.selectedExtract = response.data;
    },

    // Gets actual extract instances
    async fetchExtractInstances({ extractTypes } = {}) {
      this.loading = true;

      const [err, response] = await to(axios.get('/api/extracts', { params: { extractTypes } }));

      if (err) throw new Error(err.message);

      this.extracts = response.data;
      this.loading = false;
    },

    // This a temporary wrapper while we migrate stuff into Vue3.
    // This should be removed once we remove vue2 reporting and just update the `CompoundExtract`
    // actions to call `fetchExtractInstances` method instead
    fetchCompoundExtracts() {
      return this.fetchExtractInstances();
    },

    async fetchExtractInstancesByType({ extractType, extractSettingsId }) {
      this.loading = true;
      const [err, response] = await to(
        axios.get(`/api/extracts/${extractType}?extractSettingsId=${extractSettingsId}`)
      );
      if (err) throw new Error(err.message);
      this.previousExtracts = response.data;
      this.loading = false;
    },

    /**
     *
     * @param {*} extract - The extract data to generate compound extract for
     * @param {*} extractSettingsId - The ID of the extract settings to use, if not provided, it will use the selected extract's settings ID.
     *                                This is useful for cases where the extract is being generated from a specific settings configuration.
     * @param {*} specifiedType - The type of the extract to generate, if not provided, it will default to the type of the extract object itself.
     * @param {boolean} isUploadedToPlanningHub - Whether the extract is uploaded to Planning Hub before publishing.
     * @returns {Promise<void>} - A promise that resolves when the compound extract is generated
     * @throws {Error} - Throws an error if the API call fails
     */
    async generateCompoundExtract(
      extract,
      extractSettingsId,
      specifiedType,
      isUploadedToPlanningHub
    ) {
      this.loading = true;
      const extractWithoutJobs = omit(extract, ['jobs']);
      const [err] = await to(
        axios.post(
          '/api/extracts/compound-extract',
          {
            ...extractWithoutJobs, // spread the extract data, this will also contain extractType
            extractSettingsId: extractSettingsId || this.selectedExtract.settings._id,
            extractType: specifiedType || extract.extractType,
            isUploadedToPlanningHub,
          },
          { headers: { 'x-extract-type': specifiedType || extract.extractType } }
        )
      );
      if (err) throw new Error(err.message);
      this.loading = false;
    },

    async duplicateExtract(extractData) {
      this.loading = true;

      const [err, response] = await to(
        axios.post('/api/compound-extract/', {
          type: extractData.type,
          copyFromExisting: true,
          existingSeries: extractData.existingSeries,
          existingSettingsId: extractData.existingSettingsId,
        })
      );
      if (err) throw new Error(err.message);

      this.extractSeries.push(response.data);
      this.selectedExtract = response.data;
      this.loading = false;
      return response.data;
    },

    setSelectedExtractFilter(value) {
      this.selectedExtractFilter = value;
    },

    setIsProcessing(value) {
      this.isProcessing = value;
    },

    setLoading(loading) {
      this.loading = loading;
    },

    setCanvases(canvases) {
      this.canvases = canvases;
    },

    async fetchCanvases({ live, excludeObserved }) {
      const { isSpaceTest } = useCompoundExtract();
      const workpackageScenarioSelectStore = useWorkpackageScenarioSelectStore();
      const workpackageIds = workpackageScenarioSelectStore.selectedWorkpackageIds;

      if (!size(workpackageIds)) return [];

      await workpackageScenarioSelectStore.fetchWorkpackageScenarios();
      const subType = get(this.selectedExtract, 'series.subType', null);
      const spaceTest = isSpaceTest(subType);

      const params = {
        workpackageIds,
        where: {
          live,
          excludeObserved,
          // We should fetch only final checkpoints for non space test extracts,
          // space test extracts can have non final checkpoints that they could still be relevant
          ...(!spaceTest && { isFinalCheckpoint: true }),
        },
        pick: [
          'description',
          'scenarioId',
          'clusterId',
          'storeClassId',
          'checkpointMeta',
          'hasBeenForecast',
          'live',
          'isFinalCheckpoint',
        ],
      };

      const [err, res] = await to(
        axios.get('/api/assortment-canvases/scenarios/checkpoints', { params })
      );
      if (err) throw new Error(err.message);

      // Assign additional hierarchy keys to ensure they are displayed correctly in the reporting menu
      return map(res.data, c => ({
        ...c,
        menuLevel: live ? menuLevel.canvas : menuLevel.checkpoint,
        canvasIdCombined: `${c.scenarioId}-${c.storeClassId}-${c.clusterId}`,
        bundleId: get(
          workpackageScenarioSelectStore.parentHierachyIdsByScenarioId,
          [c.scenarioId, 'bundleId'],
          null
        ),
        workpackageId: get(
          workpackageScenarioSelectStore.parentHierachyIdsByScenarioId,
          [c.scenarioId, 'workpackageId'],
          null
        ),
        createdDate: c.checkpointMeta ? c.checkpointMeta.createdDate : '',
        createdBy: c.checkpointMeta ? c.checkpointMeta.createdBy : '',
      }));
    },

    async fetchStoreClasses() {
      const workpackageScenarioSelectStore = useWorkpackageScenarioSelectStore();
      const workpackageIds = workpackageScenarioSelectStore.selectedWorkpackageIds;
      if (!size(workpackageIds)) {
        this.storeClasses = [];
        return;
      }

      const params = {
        workpackageIds,
        pick: ['storeClasses.name', 'storeClasses._id'],
      };
      const [err, res] = await to(axios.get('/api/workpackages/scenarios/furniture', { params }));
      if (err) throw new Error(err.message);

      this.storeClasses = res.data;
    },

    async fetchClusters() {
      const workpackageScenarioSelectStore = useWorkpackageScenarioSelectStore();
      const workpackageIds = workpackageScenarioSelectStore.selectedWorkpackageIds;
      if (!size(workpackageIds)) {
        this.clusters = [];
        return;
      }

      const params = {
        workpackageIds,
        pick: ['description', 'scenarioId', 'clusterId', 'storeClassId', 'name', 'clusters'],
      };

      const [err, res] = await to(axios.get('/api/workpackages/scenarios/clusters', { params }));
      if (err) throw new Error(err.message);
      this.clusters = res.data;
    },

    async loadWorkpackagesData() {
      await this.fetchStoreClasses();
      await this.fetchClusters();

      const canvases = await this.fetchCanvases({ live: true });
      const checkpoints = await this.fetchCanvases({ live: false, excludeObserved: false });

      // use same sorting as canvas page
      const [unclusteredCanvases, otherCanvases] = partition(sortBy(canvases, ['clusterId']), {
        clusterId: null,
      });

      this.setCanvases(unclusteredCanvases.concat(otherCanvases));
      this.checkpoints = checkpoints;
    },

    setSelectedRows(selectedRows) {
      this.selectedRows = selectedRows;
    },

    addSelectedRows(rowId) {
      if (this.selectedRows.includes(rowId)) return;

      this.selectedRows.push(rowId);
    },

    removeSelectedRows(rowId) {
      this.selectedRows = this.selectedRows.filter(id => id !== rowId);
    },
  },
});
